import { flow } from 'lodash';
import React, { FunctionComponent, useMemo } from 'react';
import styles from './ConditionsPurchaseTab.module.scss';
import {
    ConditionsPurchaseTabDataType,
    ConditionsPurchaseTabProps,
} from './ConditionsPurchaseTab.type';
import { getLotFreeParticipationOrgIds } from '../../../../../utils/calcIsFreeParticipation';
import toMskMoment from '../../../../../utils/toMskMoment';
import { formatPrice } from '../../../../../utils/formatPrice';
import TariffsTableBlock from '../../../../Tariffs/TariffsTableBlock';
import TariffsBottomText from '../../../../Tariffs/TariffsBottomText';
import { Purchase, PurchaseLot } from '../../../../../types/entities';

const formatLotNumber = (isCanceled: boolean, lotNumber: string) => (
    <div className={styles.lotTitle}>
        {isCanceled ? (
            <>
                <s>Лот {lotNumber}</s>
                <div className={styles.canceledLabel}>Отменен</div>
            </>
        ) : (
            <div>Лот {lotNumber}</div>
        )}
    </div>
);

const calcIsFreeLot = (lot: PurchaseLot, purchase: Purchase) => {
    return lot.lotTariffOptions.find(
        (tariffOption) =>
            (tariffOption.tariffSum === 0 &&
                tariffOption.tariffSumWithSubscription === 0) ||
            getLotFreeParticipationOrgIds(
                { ...lot, announce: purchase },
                toMskMoment
            ).includes(0)
    );
};

const ConditionsPurchaseTab: FunctionComponent<ConditionsPurchaseTabProps> = ({
    purchase,
}) => {
    const isVip = useMemo(
        () => purchase.lots?.[0].tariff.code === 'vip',
        [purchase.lots]
    );
    const data = useMemo(() => {
        return purchase.lots.reduce(
            (lots: ConditionsPurchaseTabDataType, lot) => {
                const isCanceled = lot.status.name === 'canceled';
                const titleColumn = formatLotNumber(isCanceled, lot?.number);

                let optimalColumn: string | null = '-';
                let noSubscriptionColumn = '-';

                if (lot.lotTariffOptions.length === 0) {
                    optimalColumn = 'Не тарифицируется';
                    noSubscriptionColumn = 'Не тарифицируется';
                } else {
                    const lotTariffOptions = lot.lotTariffOptions.filter(
                        (option) => option.tariffLicense.name !== 'start'
                    );

                    if (lotTariffOptions.length > 0) {
                        optimalColumn = formatPrice(
                            lotTariffOptions[0]
                                .tariffSumWithSubscriptionOptimal / 100
                        );
                        noSubscriptionColumn = formatPrice(
                            lotTariffOptions[0].tariffSum / 100
                        );
                    }

                    if (lotTariffOptions[0].tariffSum === 0) {
                        optimalColumn = 'Бесплатно';
                        noSubscriptionColumn = 'Бесплатно';

                        // для старых закупок есть условие если IS_WITH_SUBSCRIPTION_ONLY=1 и TARIFF_SUM=0 то прочерк в поле без подписки
                        if (lotTariffOptions[0].isWithSubscriptionOnly) {
                            noSubscriptionColumn = '-';
                        }
                    }
                    if (isVip) {
                        optimalColumn = null;
                    }
                }

                lots.push({
                    columns: [
                        titleColumn,
                        optimalColumn,
                        noSubscriptionColumn,
                    ].filter(
                        Boolean
                    ) as ConditionsPurchaseTabDataType[0]['columns'],
                });

                return lots;
            },
            []
        );
    }, [isVip, purchase.lots]);
    return (
        <div className={styles.container}>
            <div className={styles.tariffs}>
                <TariffsTableBlock isVip={isVip}>
                    {data.map((row: any, i: number) => {
                        return (
                            <tr key={i}>
                                {row?.columns?.map(
                                    (column: string, i: number) => (
                                        <td key={i}>{column}</td>
                                    )
                                )}
                            </tr>
                        );
                    })}
                </TariffsTableBlock>

                <TariffsBottomText />
            </div>
        </div>
    );
};

export default flow(() => ConditionsPurchaseTab)();
